import * as z from 'zod'
import {
    injuredPedestrianValidator,
    injuredPersonValidator,
    otherPartyInjuredPersonValidator,
    otherPartyVehicleValidator,
    witnessValidator,
} from '~/lib/zod/multi-person-fields/validator'

import {
    ACCEPTED_FILE_TYPES,
    MAX_FILE_SIZE_BYTES,
    MAX_FILE_SIZE_MB,
} from '~/models/generalConstants'

export const aboutYouValidator = z
    .object({
        PolicyNumber: z.string(),
        PolicyholderFirstName: z.string(),
        PolicyholderLastName: z.string(),
        partyReportingIncident: z.string(),
        insuranceProvider: z.string(),

        ReporterFirstName: z.string(),
        ReporterLastName: z.string(),
        relationshipToHolder: z.string(),
        ReporterAddressType: z.string(),
        ReporterAddressLine1: z.string(),
        ReporterAddressLine2: z.string().optional(),
        ReporterCountry: z.string(),
        ReporterParish: z.string().optional(),
        ReporterDistrict: z.string().optional(),
        ReporterIdentificationType: z.string(),
        ReporterIdentificationNumber: z.string(),
        ReporterPhoneType: z.string(),
        reportingPhoneNumber: z.string(),
        ReporterEmailType: z.string(),
        reportingEmailAddress: z.string().email(),
        ReporterContactPreference: z.string(),
        AreYouDriver: z.string(),
    })
    .superRefine((data, refinementContext) => {
        if (
            (data.ReporterCountry === 'barbados' ||
                data.ReporterCountry === 'jamaica') &&
            !data.ReporterParish
        ) {
            return refinementContext.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Required',
                path: ['ReporterParish'],
            })
        }
    })

export const aboutDriverValidator = z
    .object({
        AreYouPolicyHolder: z.string(),
        AreYouDriver: z.string(),

        relationshipToHolder: z.string(),
        DriverFirstName: z.string(),
        DriverLastName: z.string(),
        DriverDateOfBirth: z.date().refine((date) => date <= new Date(), {
            message: 'Date cannot be in the future',
        }),
        phoneType: z.string(),
        DriverPhoneNumber: z.string(),
        emailType: z.string(),
        DriverEmailAddress: z.string().email(),
        DriverContactPreference: z.string(),
        bestContactTime: z
            .string()
            .refine(
                (value) => {
                    if (value) {
                        const [hour, minute] = value.split(':').map(Number)

                        return (
                            (hour > 8 || (hour === 8 && minute >= 0)) &&
                            (hour < 18 || (hour === 18 && minute === 0))
                        )
                    }
                },
                {
                    message:
                        'Please choose a time between 8:00 a.m. and 6:00 p.m.',
                }
            )
            .optional(),
        gaveDriverConsent: z.string().optional(),
        DriverAddressType: z.string(),
        DriverAddressLine1: z.string(),
        DriverAddressLine2: z.string().optional(),
        DriverCountry: z.string(),
        DriverParish: z.string().optional(),
        DriverDistrict: z.string().optional(),
        DriverLicenseType: z.string(),
        LicenseNumber: z.string(),
        DateFirstLicensed: z.date().refine((date) => date <= new Date(), {
            message: 'Date cannot be in the future',
        }),
        CurrentLicenseIssueDate: z
            .date()
            .refine((date) => date <= new Date(), {
                message: 'Date cannot be in the future',
            })
            .optional(),
        LicenseExpirationDate: z.date(),
        LicenseCountry: z.string(),
        LicenseStatus: z.string(),
    })
    .superRefine((data, refinementContext) => {
        if (data.DriverLicenseType === 'permanent') {
            if (!data.CurrentLicenseIssueDate) {
                return refinementContext.addIssue({
                    code: z.ZodIssueCode.custom,
                    message:
                        'Please state the current license issue date if you have a permanent license',
                    path: ['CurrentLicenseIssueDate'],
                })
            }
        }

        if (
            (data.DriverCountry === 'barbados' ||
                data.DriverCountry === 'jamaica') &&
            !data.DriverParish
        ) {
            return refinementContext.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Required',
                path: ['DriverParish'],
            })
        }

        if (data.AreYouDriver === 'no' && !data.gaveDriverConsent) {
            return refinementContext.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Required',
                path: ['gaveDriverConsent'],
            })
        }
    })

export const whatHappenedValidator = z
    .object({
        DateOfEvent: z.date().refine((date) => date <= new Date(), {
            message: 'Date cannot be in the future',
        }),
        TimeOfAccident: z.string(),
        AddressLine1: z.string(),
        AddressLine2: z.string().optional(),
        Parish: z.string(),

        DirectionOfTravel: z.string().optional(),
        otherDirectionOfTravel: z.string().optional(),
        numberOfOccupants: z.string(),
        areYouResponsible: z.string(),
        explanation: z.string().optional(),

        reportedToPolice: z.string(),
        dateReportedToPolice: z.date().optional(),
        PoliceStation: z.string().optional(),
        InvestigatingOfficer: z.string().optional(),
        InvestigatingOfficerContact: z.string().optional(),

        activityAtTimeOfAccident: z.string().optional(),

        whereIsVehicle: z.string(),
        wasWreckerUsed: z.string().optional(),
        wreckerName: z.string().optional(),
        wreckerNumber: z.string().optional(),

        conditionOfRoad: z.string().optional(),
        speed: z.string().optional(),
        weather: z.string().optional(),
        streetLighting: z.string().optional(),
        visibility: z.string().optional(),
        DetailsOfAccident: z.string(),
    })
    .superRefine(
        (
            {
                reportedToPolice,
                dateReportedToPolice,
                PoliceStation,
                InvestigatingOfficer,
            },
            refinementContext
        ) => {
            if (reportedToPolice === 'yes') {
                if (!dateReportedToPolice) {
                    refinementContext.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['dateReportedToPolice'],
                    })
                }

                if (!PoliceStation) {
                    refinementContext.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['PoliceStation'],
                    })
                }

                if (!InvestigatingOfficer) {
                    refinementContext.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['InvestigatingOfficer'],
                    })
                }
            }
        }
    )

export const aboutVehicleValidator = z
    .object({
        VehicleYear: z.number(),
        VehicleMake: z.string(),
        VehicleModel: z.string(),
        VehicleRegistrationNumber: z.string(),
        VehicleChassisNumber: z.string(),
        VehicleEngineNumber: z.string().optional(),
        VehicleMortgagee: z.string(),
        nameFinanceCompany: z.string().optional(),
        branchFinanceCompany: z.string().optional(),
        bestContactPerson: z.string().optional(),
        phoneNumber: z.string().optional(),
    })
    .superRefine(
        (
            { VehicleMortgagee, nameFinanceCompany, branchFinanceCompany },
            refinementContext
        ) => {
            if (VehicleMortgagee === 'yes') {
                if (!nameFinanceCompany) {
                    refinementContext.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['nameFinanceCompany'],
                    })
                }

                if (!branchFinanceCompany) {
                    refinementContext.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['branchFinanceCompany'],
                    })
                }
            }
        }
    )

export const damageDetailsValidator = z
    .object({
        VehicleMortgagee: z.string(),
        isVehicleDamaged: z.string(),
        OwnerDamageDetails: z
            .array(z.string())
            .min(
                1,
                'Since your vehicle is damaged you have to select at least one area of damage.'
            )
            .optional(),
        isClaimForDamage: z.string().optional(),
        settlementOption: z.string().optional(),
        preferredRepairer: z.string().optional(),
        accountHolderName: z.string().optional(),
        bankAccountType: z.string().optional(),
        bankName: z.string().optional(),
        bankBranch: z.string().optional(),
        bankAccountNumber: z.string().optional(),
        routingNumber: z.string().optional(),
        isDeclarationApproved: z.string().optional(),
        additionalDamageDetails: z.string().optional(),
        isPointOfImpact: z.string().optional(),
        isVehicleDrivable: z.string(),

        otherPartyVehicles: z.array(otherPartyVehicleValidator).optional(),
    })
    .superRefine(
        (
            {
                isVehicleDamaged,
                OwnerDamageDetails,
                isClaimForDamage,
                settlementOption,
                preferredRepairer,
                accountHolderName,
                bankAccountType,
                bankName,
                bankBranch,
                bankAccountNumber,
                isDeclarationApproved,
                VehicleMortgagee,
            },
            refinementContext
        ) => {
            if (isVehicleDamaged === 'yes' && !OwnerDamageDetails) {
                return refinementContext.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'You have to select at least one item.',
                    path: ['OwnerDamageDetails'],
                })
            }

            if (isClaimForDamage === 'yes') {
                if (!settlementOption) {
                    refinementContext.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['settlementOption'],
                    })
                }

                if (settlementOption === 'repairs') {
                    if (!preferredRepairer) {
                        refinementContext.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Required',
                            path: ['preferredRepairer'],
                        })
                    }
                }

                if (settlementOption === 'cash') {
                    if (VehicleMortgagee === 'no') {
                        if (!accountHolderName) {
                            refinementContext.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: 'Required',
                                path: ['accountHolderName'],
                            })
                        }

                        if (!bankAccountType) {
                            refinementContext.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: 'Required',
                                path: ['bankAccountType'],
                            })
                        }

                        if (!bankName) {
                            refinementContext.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: 'Required',
                                path: ['bankName'],
                            })
                        }

                        if (!bankBranch) {
                            refinementContext.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: 'Required',
                                path: ['bankBranch'],
                            })
                        }

                        if (!bankAccountNumber) {
                            refinementContext.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: 'Required',
                                path: ['bankAccountNumber'],
                            })
                        }
                    }

                    if (!isDeclarationApproved) {
                        refinementContext.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Required',
                            path: ['isDeclarationApproved'],
                        })
                    }
                }
            }
        }
    )

export const otherDetailsValidator = z.object({
    wasAmbulanceOnScene: z.string().optional(),

    isAnyoneInjured: z.string(),

    injuredPersons: z.array(injuredPersonValidator).optional(),

    otherPartyIsAnyoneInjured: z.string(),

    otherPartyInjuredPersons: z
        .array(otherPartyInjuredPersonValidator)
        .optional(),
    isPedestrianInjured: z.string(),
    pedestrians: z.array(injuredPedestrianValidator).optional(),

    isWitness: z.string(),
    witnesses: z.array(witnessValidator).optional(),
    isAccidentFootageAvailable: z.string().optional(),
    SupportingDocument: z
        .array(
            z
                .instanceof(File)
                .refine((file) => {
                    if (file?.type) {
                        return ACCEPTED_FILE_TYPES.includes(file.type)
                    }
                }, 'File must be a pdf, jpeg, png or mp4')
                .refine((file) => {
                    if (file?.size) {
                        return file.size <= MAX_FILE_SIZE_BYTES
                    }
                }, `File must be ${MAX_FILE_SIZE_MB} MB or less in size`)
        )
        .optional(),
})
